import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import { useEffect } from 'react';

import { Button, Typography } from '@/shared/components/atoms';
import { IConfirmationModalProps } from '@/shared/components/molecules/ConfirmationModal/IConfirmationModal';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import CheckMark from '~/images/shared/CheckMarkWithoutBG.svg';
import Trash from '~/images/shared/Trash.svg';
import Warning from '~/images/shared/Wraning.svg';

const ConfirmationModal: React.FC<IConfirmationModalProps> = ({
  children,
  width,
  variant,
  confirmText,
  dismissText,
  onConfirm,
  onDismiss,
  onConfirmRemoveNumber,
  onDismissRemoveNumber,
  icon,
  header,
  message,
  preferredAction = 'confirm',
  buttonsAlignment = 'row',
  reverseButtonOrder,
  autoDismissAfter,
}) => {
  const { t } = useTranslation();
  const { closeModal } = useModal();

  const shouldDismiss = preferredAction === 'dismiss' || !onConfirm;

  const handleDismiss = () => {
    if (onDismiss) onDismiss();
    closeModal(onDismissRemoveNumber);
  };

  const handleConfirm = () => {
    closeModal(onConfirmRemoveNumber);
    if (onConfirm) onConfirm();
  };

  useEffect(() => {
    if (autoDismissAfter) {
      const timeout = setTimeout(handleDismiss, autoDismissAfter);
      return () => {
        clearTimeout(timeout);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className={classNames(
        'flex flex-col items-center gap-32 text-center',
        width ?? 'tablet:w-343'
      )}
    >
      <div
        className={classNames(
          'flex h-64 w-64 items-center justify-center rounded-full',
          variant === 'warning'
            ? 'bg-neutralVariant-96 text-tertiary-20'
            : variant === 'danger'
            ? 'bg-error-90 text-error-20'
            : 'bg-secondary-90 text-green-300'
        )}
      >
        {icon ??
          (variant === 'warning' ? (
            <Warning width='35' height='35' />
          ) : variant === 'danger' ? (
            <Trash width='35' height='35' />
          ) : (
            <CheckMark width='35' height='35' />
          ))}
      </div>
      <div>
        <Typography variant='h2' weight='medium'>
          {header}
        </Typography>
        {message && (
          <Typography variant='subtitle' className='mt-8 text-surface-10'>
            {message}
          </Typography>
        )}
      </div>
      {children}
      <div
        className={classNames(
          'flex w-full gap-8',
          buttonsAlignment === 'col' && 'flex-col'
        )}
      >
        <Button
          variant={shouldDismiss ? 'filled' : 'text'}
          size='full'
          className={classNames(
            'h-56 !rounded-xl',
            reverseButtonOrder && 'order-2',
            !shouldDismiss && '!border-xs border-outline-default'
          )}
          onClick={handleDismiss}
        >
          {t(dismissText ?? 'Dismiss')}
        </Button>
        {(onConfirm || onConfirmRemoveNumber) && (
          <Button
            variant={shouldDismiss ? 'text' : 'filled'}
            size='full'
            className={classNames(
              'h-56 !rounded-xl',
              shouldDismiss
                ? '!border-xs border-outline-default'
                : variant === 'danger' && '!bg-error-400'
            )}
            onClick={handleConfirm}
          >
            {t(confirmText ?? 'confirm')}
          </Button>
        )}
      </div>
    </div>
  );
};

export default ConfirmationModal;
